import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus, ShoppingCart, Eye, Edit, Trash2, Settings,
  CheckCircle, XCircle, Clock, Package, Truck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PurchaseOrders = () => {
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  
  const [formData, setFormData] = useState({
    supplier_name: '',
    supplier_email: '',
    supplier_phone: '',
    items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
    subtotal: 0,
    tax: 0,
    total: 0,
    required_by_date: '',
    notes: '',
    internal_notes: '',
    status: 'Draft'
  });

  const [notificationSettings, setNotificationSettings] = useState({
    finance_manager_email: '',
    finance_manager_name: '',
    admin_email: '',
    admin_name: '',
    notify_on_submission: true,
    notify_on_approval: true,
    notify_on_completion: true
  });

  const statuses = ['All', 'Draft', 'Submitted', 'Approved', 'Ordered', 'Received', 'Completed', 'Cancelled'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle both array response and legacy object response
      const data = Array.isArray(response.data) ? response.data : (response.data.purchase_orders || []);
      setPos(data);
    } catch (error) {
      console.error('Failed to load purchase orders:', error);
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const loadNotificationSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/purchase-orders/settings/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationSettings(response.data);
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    }
  };

  const handleOpenForm = (po = null) => {
    if (po) {
      setEditingPO(po);
      setFormData({
        ...po,
        required_by_date: po.required_by_date ? new Date(po.required_by_date).toISOString().split('T')[0] : ''
      });
    } else {
      setEditingPO(null);
      setFormData({
        supplier_name: '',
        supplier_email: '',
        supplier_phone: '',
        items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
        subtotal: 0,
        tax: 0,
        total: 0,
        required_by_date: '',
        notes: '',
        internal_notes: '',
        status: 'Draft'
      });
    }
    setShowFormModal(true);
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, total: 0 }]
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
    calculateTotals(newItems, formData.tax);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(newItems[index].unit_price) || 0);
    }
    
    setFormData({ ...formData, items: newItems });
    calculateTotals(newItems, formData.tax);
  };

  const calculateTotals = (items, tax) => {
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const taxAmount = (subtotal * (parseFloat(tax) || 0)) / 100;
    const total = subtotal + taxAmount;
    
    setFormData(prev => ({
      ...prev,
      subtotal,
      tax: parseFloat(tax) || 0,
      total
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        required_by_date: formData.required_by_date ? new Date(formData.required_by_date).toISOString() : null
      };

      if (editingPO) {
        await axios.put(`${API}/purchase-orders/${editingPO.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Purchase order updated successfully!');
      } else {
        await axios.post(`${API}/purchase-orders`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Purchase order created successfully!');
      }

      setShowFormModal(false);
      loadData();
    } catch (error) {
      console.error('Failed to save purchase order:', error);
      toast.error('Failed to save purchase order');
    }
  };

  const handleStatusChange = async (poId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/purchase-orders/${poId}/status`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { new_status: newStatus }
      });
      toast.success(`Status updated to ${newStatus}`);
      loadData();
      if (showDetailModal) {
        const response = await axios.get(`${API}/purchase-orders/${poId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedPO(response.data);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id, poNumber) => {
    if (!window.confirm(`Are you sure you want to delete ${poNumber}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/purchase-orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Purchase order deleted');
      loadData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete purchase order');
    }
  };

  const handleViewPO = async (po) => {
    setSelectedPO(po);
    setShowDetailModal(true);
  };

  const handleOpenSettings = async () => {
    await loadNotificationSettings();
    setShowSettingsModal(true);
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/purchase-orders/settings/notifications`, notificationSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notification settings updated!');
      setShowSettingsModal(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      Draft: 'secondary',
      Submitted: 'default',
      Approved: 'default',
      Ordered: 'default',
      Received: 'default',
      Completed: 'default',
      Cancelled: 'destructive'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const filteredPOs = filterStatus === 'All'
    ? pos
    : pos.filter(po => po.status === filterStatus);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight uppercase text-gray-900">
            Purchase Orders
          </h1>
          <p className="text-gray-700 mt-1 font-medium">
            Manage procurement requests and supplier orders
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleOpenSettings}>
            <Settings size={16} className="mr-2" />
            Settings
          </Button>
          <Button onClick={() => handleOpenForm()}>
            <Plus size={16} className="mr-2" />
            New PO
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-2 border-gray-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{pos.length}</p>
              <p className="text-xs text-gray-600 mt-1">Total POs</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">
                {pos.filter(p => p.status === 'Submitted').length}
              </p>
              <p className="text-xs text-gray-700 mt-1">Pending Approval</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {pos.filter(p => p.status === 'Approved').length}
              </p>
              <p className="text-xs text-gray-700 mt-1">Approved</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-purple-200 bg-purple-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">
                {pos.filter(p => p.status === 'Ordered').length}
              </p>
              <p className="text-xs text-gray-700 mt-1">Ordered</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {pos.filter(p => p.status === 'Completed').length}
              </p>
              <p className="text-xs text-gray-700 mt-1">Completed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Label className="text-sm font-semibold text-gray-900">Filter by Status:</Label>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statuses.map((status) => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* PO List */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-700">
            Loading purchase orders...
          </CardContent>
        </Card>
      ) : filteredPOs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ShoppingCart size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-900 mb-2 font-semibold text-lg">No purchase orders found</p>
            <p className="text-sm text-gray-700 mb-4">
              Create your first purchase order to start tracking procurement
            </p>
            <Button onClick={() => handleOpenForm()}>
              <Plus size={16} className="mr-2" />
              Create First PO
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredPOs.map((po) => (
            <Card key={po.id} className="hover:border-primary transition-all">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-sm font-mono font-semibold text-gray-900">
                        {po.po_number}
                      </p>
                      {getStatusBadge(po.status)}
                    </div>
                    <p className="text-lg font-semibold text-gray-900 mb-1">
                      {po.supplier_name}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3">
                      <div>
                        <p className="text-gray-600 text-xs">Requested By</p>
                        <p className="text-gray-900 font-medium">{po.requester_name}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-xs">Items</p>
                        <p className="text-gray-900 font-medium">{po.items?.length || 0}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-xs">Total Amount</p>
                        <p className="text-gray-900 font-bold">${po.total?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-xs">Date</p>
                        <p className="text-gray-900 font-mono text-xs">
                          {new Date(po.request_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleViewPO(po)}
                      className="text-blue-500"
                    >
                      <Eye size={16} />
                    </Button>
                    {po.status === 'Draft' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleOpenForm(po)}
                        className="text-gray-500"
                      >
                        <Edit size={16} />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(po.id, po.po_number)}
                      className="text-red-500"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit PO Modal - Part 1 continues in next message due to length */}
      <Dialog open={showFormModal} onOpenChange={setShowFormModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 text-xl">
              {editingPO ? 'Edit Purchase Order' : 'Create Purchase Order'}
            </DialogTitle>
            <DialogDescription className="text-gray-700">
              Fill in the details for the purchase order
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Supplier Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Supplier Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-3 md:col-span-1">
                    <Label className="text-sm font-semibold text-gray-900">Supplier Name *</Label>
                    <Input
                      value={formData.supplier_name}
                      onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                      required
                      placeholder="Supplier or vendor name"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-gray-900">Email</Label>
                    <Input
                      type="email"
                      value={formData.supplier_email}
                      onChange={(e) => setFormData({ ...formData, supplier_email: e.target.value })}
                      placeholder="supplier@example.com"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-gray-900">Phone</Label>
                    <Input
                      value={formData.supplier_phone}
                      onChange={(e) => setFormData({ ...formData, supplier_phone: e.target.value })}
                      placeholder="555-1234"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Line Items</CardTitle>
                  <Button type="button" size="sm" variant="outline" onClick={handleAddItem}>
                    <Plus size={14} className="mr-1" />
                    Add Item
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-5">
                        <Label className="text-xs">Description</Label>
                        <Input
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Item description"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Qty</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Unit Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Total</Label>
                        <Input
                          value={`$${item.total.toFixed(2)}`}
                          disabled
                          className="bg-gray-50"
                        />
                      </div>
                      <div className="col-span-1">
                        {formData.items.length > 1 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveItem(index)}
                            className="text-red-500"
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 space-y-2 border-t pt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Subtotal:</span>
                    <span className="font-semibold text-gray-900">${formData.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm items-center">
                    <span className="text-gray-700">Tax (%):</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.tax}
                      onChange={(e) => calculateTotals(formData.items, e.target.value)}
                      className="w-20 h-8"
                    />
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span className="text-gray-900">Total:</span>
                    <span className="text-gray-900">${formData.total.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-gray-900">Required By Date</Label>
                <Input
                  type="date"
                  value={formData.required_by_date}
                  onChange={(e) => setFormData({ ...formData, required_by_date: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-gray-900">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.filter(s => s !== 'All').map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-900">Notes (for supplier)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                placeholder="Any special instructions or notes for the supplier..."
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-900">Internal Notes</Label>
              <Textarea
                value={formData.internal_notes}
                onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })}
                rows={2}
                placeholder="Internal notes (not visible to supplier)..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowFormModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingPO ? 'Update' : 'Create'} Purchase Order
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View PO Detail Modal */}
      {showDetailModal && selectedPO && (
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="text-gray-900 text-2xl font-black">
                    {selectedPO.po_number}
                  </DialogTitle>
                  <DialogDescription>{selectedPO.supplier_name}</DialogDescription>
                </div>
                {getStatusBadge(selectedPO.status)}
              </div>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Basic Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Order Information</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 text-xs">Requester</p>
                    <p className="font-semibold text-gray-900">{selectedPO.requester_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 text-xs">Request Date</p>
                    <p className="font-mono text-gray-900">{new Date(selectedPO.request_date).toLocaleDateString()}</p>
                  </div>
                  {selectedPO.required_by_date && (
                    <div>
                      <p className="text-gray-600 text-xs">Required By</p>
                      <p className="font-mono text-gray-900">{new Date(selectedPO.required_by_date).toLocaleDateString()}</p>
                    </div>
                  )}
                  {selectedPO.completed_date && (
                    <div>
                      <p className="text-gray-600 text-xs">Completed</p>
                      <p className="font-mono text-gray-900">{new Date(selectedPO.completed_date).toLocaleDateString()}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Line Items */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Items Ordered</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {selectedPO.items?.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{item.description}</p>
                          <p className="text-xs text-gray-600">Qty: {item.quantity} × ${item.unit_price}</p>
                        </div>
                        <p className="text-sm font-bold text-gray-900">${item.total.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 space-y-1 border-t pt-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Subtotal:</span>
                      <span className="text-gray-900">${selectedPO.subtotal?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Tax:</span>
                      <span className="text-gray-900">${((selectedPO.total || 0) - (selectedPO.subtotal || 0)).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span className="text-gray-900">Total:</span>
                      <span className="text-gray-900">${selectedPO.total?.toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              {selectedPO.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700">{selectedPO.notes}</p>
                  </CardContent>
                </Card>
              )}

              {selectedPO.internal_notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Internal Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700 italic">{selectedPO.internal_notes}</p>
                  </CardContent>
                </Card>
              )}

              {/* Status Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Update Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {selectedPO.status === 'Draft' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusChange(selectedPO.id, 'Submitted')}
                      >
                        Submit for Approval
                      </Button>
                    )}
                    {selectedPO.status === 'Submitted' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleStatusChange(selectedPO.id, 'Approved')}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleStatusChange(selectedPO.id, 'Cancelled')}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    {selectedPO.status === 'Approved' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusChange(selectedPO.id, 'Ordered')}
                      >
                        Mark as Ordered
                      </Button>
                    )}
                    {selectedPO.status === 'Ordered' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusChange(selectedPO.id, 'Received')}
                      >
                        Mark as Received
                      </Button>
                    )}
                    {selectedPO.status === 'Received' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusChange(selectedPO.id, 'Completed')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Complete PO
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-gray-900 text-xl">PO Notification Settings</DialogTitle>
            <DialogDescription className="text-gray-700">
              Configure who receives alerts when purchase orders are completed
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveSettings} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Finance Manager</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-sm font-semibold text-gray-900">Name</Label>
                  <Input
                    value={notificationSettings.finance_manager_name}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      finance_manager_name: e.target.value
                    })}
                    placeholder="Finance Manager Name"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-900">Email</Label>
                  <Input
                    type="email"
                    value={notificationSettings.finance_manager_email}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      finance_manager_email: e.target.value
                    })}
                    placeholder="finance@company.com"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Admin</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-sm font-semibold text-gray-900">Name</Label>
                  <Input
                    value={notificationSettings.admin_name}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      admin_name: e.target.value
                    })}
                    placeholder="Admin Name"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-900">Email</Label>
                  <Input
                    type="email"
                    value={notificationSettings.admin_email}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      admin_email: e.target.value
                    })}
                    placeholder="admin@company.com"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Notification Triggers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="notify_submission"
                    checked={notificationSettings.notify_on_submission}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      notify_on_submission: e.target.checked
                    })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="notify_submission" className="text-sm cursor-pointer">
                    Notify when PO is submitted for approval
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="notify_approval"
                    checked={notificationSettings.notify_on_approval}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      notify_on_approval: e.target.checked
                    })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="notify_approval" className="text-sm cursor-pointer">
                    Notify when PO is approved
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="notify_completion"
                    checked={notificationSettings.notify_on_completion}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      notify_on_completion: e.target.checked
                    })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="notify_completion" className="text-sm cursor-pointer">
                    Notify when PO is completed
                  </Label>
                </div>
              </CardContent>
            </Card>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSettingsModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Save Settings
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrders;
